import { RvHookup as TrailersIcon } from "@mui/icons-material";
import { Box, Switch, Tooltip, Typography } from "@mui/material";
import React from "react";
import { AnyTrackLocation } from "../../../../api/anyTrackApi";
import { useSidebarInfiniteScroll } from "../../hooks/useSidebarInfiniteScroll";
import { useTrailerFilter } from "../../hooks/useTrailerFilter";
import { SidebarShell } from "./SidebarShell";
import { TrailerList } from "./TrailerList";

interface TrailerSidebarProps {
  trailers: AnyTrackLocation[];
  selectedTrailerId?: string;
  onTrailerSelect: (deviceId: string) => void;
  isLoading?: boolean;
  isAnyTrackEnabled?: boolean;
  showTrailers?: boolean;
  onShowTrailersToggle?: () => void;
  vehicleSidebarOpen: boolean;
  onSidebarToggle?: (isOpen: boolean) => void;
  defaultOpen?: boolean;
}

const VEHICLE_SIDEBAR_WIDTH = 280;
const SIDEBAR_WIDTH = 260;

export const TrailerSidebar: React.FC<TrailerSidebarProps> = ({
  trailers,
  selectedTrailerId,
  onTrailerSelect,
  isLoading = false,
  isAnyTrackEnabled = false,
  showTrailers = false,
  onShowTrailersToggle,
  vehicleSidebarOpen,
  onSidebarToggle,
  defaultOpen = true,
}) => {
  const { searchQuery, setSearchQuery, filteredTrailers } = useTrailerFilter({
    trailers,
  });
  const {
    displayedItems: displayedTrailers,
    totalCount,
    hasMore,
    isLoadingMore,
    sentinelRef,
    listRef,
  } = useSidebarInfiniteScroll(filteredTrailers, undefined, searchQuery);

  const leftOffset = vehicleSidebarOpen ? VEHICLE_SIDEBAR_WIDTH : 0;
  const isSearchDisabled = isLoading || !isAnyTrackEnabled;

  const headerExtra = onShowTrailersToggle ? (
    <Tooltip title="Show trailers on map">
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.25,
          px: 0.5,
          borderRadius: 1,
          border: 1,
          borderColor: showTrailers ? "warning.main" : "divider",
          bgcolor: showTrailers ? "warning.light" : "background.paper",
          flexShrink: 0,
        }}
      >
        <TrailersIcon
          sx={{
            fontSize: "0.85rem",
            color: showTrailers ? "warning.dark" : "text.secondary",
          }}
        />
        <Typography
          variant="caption"
          sx={{
            color: showTrailers ? "warning.dark" : "text.secondary",
            fontWeight: 600,
            fontSize: "0.65rem",
            lineHeight: 1,
            whiteSpace: "nowrap",
          }}
        >
          Show
        </Typography>
        <Switch
          size="small"
          checked={showTrailers}
          onChange={onShowTrailersToggle}
          disabled={!isAnyTrackEnabled}
          inputProps={{ "aria-label": "Show trailers on map" }}
          sx={{
            ml: -0.5,
            "& .MuiSwitch-switchBase.Mui-checked": {
              color: "warning.main",
              "& + .MuiSwitch-track": {
                backgroundColor: "warning.main",
              },
            },
          }}
        />
      </Box>
    </Tooltip>
  ) : null;

  return (
    <SidebarShell
      title="Trailers"
      icon={<TrailersIcon sx={{ fontSize: 15 }} />}
      totalCount={totalCount}
      displayedCount={displayedTrailers.length}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      searchPlaceholder="Search trailers..."
      isLoading={isSearchDisabled}
      isLoadingMore={isAnyTrackEnabled ? isLoadingMore : false}
      width={SIDEBAR_WIDTH}
      leftOffset={leftOffset}
      zIndex={1310}
      toggleButtonTop="18%"
      toggleButtonColor="warning"
      onSidebarToggle={onSidebarToggle}
      defaultOpen={defaultOpen}
      headerExtra={headerExtra}
    >
      <TrailerList
        trailers={displayedTrailers}
        selectedTrailerId={selectedTrailerId ?? null}
        onTrailerSelect={onTrailerSelect}
        isLoading={isLoading}
        isAnyTrackEnabled={isAnyTrackEnabled}
        sentinelRef={sentinelRef}
        listRef={listRef}
        isLoadingMore={isAnyTrackEnabled ? isLoadingMore : false}
        hasMore={isAnyTrackEnabled && hasMore}
      />
    </SidebarShell>
  );
};
